import { compact, compose, isEmpty, negate, overEvery, update, map, values, reduce } from 'lodash/fp';
import { pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { AppClockServiceProps, createAction, lt, showErrorAlert, showSuccessAlert } from '@mews/framework';
import { createUUID } from '@mews/std';
import { RouteName } from '@mews-commander/enums';
import { ServicePricing, VoucherType } from '@mews-commander/data-access';
import {
    ApplicationState,
    AsyncAction,
    CommanderThunkAction,
    OccupancyData,
    WithScope,
    WithServiceId,
} from '@mews-commander/types';
import { createFetchAction, isDifferenceEmpty } from '@mews-commander/utils';
import { validation as validate } from '@mews-commander/legacy-form';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { fetchRates, getServiceRateIds, hasServiceRates } from '@mews-commander/legacy-entities/src/ducks/rates';
import { getResourceCategoryIds } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import {
    getEnterpriseChainId,
    isEnterpriseMultiTaxEnvironment,
} from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { fetchServiceVouchers, setVouchers } from '@mews-commander/legacy-entities/src/ducks/vouchers';
import { getEnterpriseProfileId } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { fetchActiveEnterpriseServices } from '@mews-commander/legacy-entities/src/actions/services';
import { getServiceEnterpriseId } from '@mews-commander/legacy-entities/src/ducks/services';
import { getCurrentEnterpriseChainId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getAvailabilityBlock } from '@mews-commander/legacy-entities/src/ducks/availabilityBlocks';
import {
    getSelectedServiceUserProfileId,
    isCrossEnterpriseReservation,
    getResourceCategoryIdsOfSelectedResourceCategoryType,
} from '../selectors/entities';
import {
    getPricingOptions,
    getReservationGroup,
    getReservation,
    getOptionConstraints,
    getOptionValues,
    getServiceStartDateUtc,
    getServiceEndDateUtc,
    getCollidingReservationsByCategory,
    getDateRangeKeyFromOrder,
    hasServiceAvailableCategories,
    getReservationWithCustomPrice,
    getServiceId,
    isSelectedEnterpriseInMultiTaxEnvironment,
    hasReservationPrice,
    getReservationProductCounts,
    getSelectedAvailabilityBlockId,
    getReservationsMinimalArrivalUtc,
    isDefinite,
    getShowDateServiceDiscrepancyBanner,
    getShowTimeServiceDiscrepancyBanner,
    getTravelAgencyId,
} from '../selectors';
import { processCategoryAvailabilities } from '../utils/func';
import { mapReservationDto } from '../utils/reservations';
import { getRateIdsFromServicePricing } from '../utils/pricing';
import { mapProductCountsToArray } from '../utils/products';
import { navigateInModal } from '../../app/actions/common';
import {
    NEW_RESERVATION_SCREEN_EVENT_CATEGORY,
    TIME_TO_RESERVATION_END_MARK,
    TIME_TO_RESERVATION_START_MARK,
} from '../constants';
import { handleErrorPure } from '../../../middleware/errorHandler';
import {
    PRICING_FETCH_STARTED,
    PRICING_FETCH_FAILED,
    PRICING_FETCH_SUCCEEDED,
    CREATE_RESERVATION_GROUP,
    AVAILABILITY_FETCH_STARTED,
    AVAILABILITY_FETCH_SUCCEEDED,
    AVAILABILITY_FETCH_FAILED,
    OPTIONS_VALIDATED,
    CONFIRMATION_SHOWN,
    CUSTOM_PRICE_FETCH,
    CUSTOM_PRICE_CLEARED,
    BASKET_PRICE_FETCH,
} from './types';

export const fetchReservationPrice =
    ({ reservationKey, scope, serviceId, invalidatePrice = false }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const { reservationComponent } = container();
        const state = getState();

        // @ts-expect-error $TSFixMeError: Argument of type '{ scope: any; reservationKey: any; }' is not assignable to parameter of type '{ reservationKey: string...
        if (!invalidatePrice && hasReservationPrice(state, { scope, reservationKey })) {
            return;
        }

        // @ts-expect-error $TSFixMeError: Argument of type '{ scope: any; id: any; }' is not assignable to parameter of type '{ id: string; }'.
        const enterpriseId = getServiceEnterpriseId(state, { scope, id: serviceId });
        const profileId = getEnterpriseProfileId(state, { enterpriseId });
        const travelAgencyId = getTravelAgencyId(state, { scope });

        // @ts-expect-error $TSFixMeError: Argument of type '{ scope: any; reservationKey: any; }' is not assignable to parameter of type '{ reservationKey: string...
        const reservation = mapReservationDto(getReservation(state, { scope, reservationKey }));
        // @ts-expect-error $TSFixMeError: Argument of type '{ scope: any; reservationKey: any; }' is not assignable to parameter of type '{ reservationKey: string...
        const productOrders = mapProductCountsToArray(getReservationProductCounts(state, { scope, reservationKey }));

        const reservations = [{ ...reservation, serviceId, productOrders, travelAgencyId }];

        const promise = reservationComponent.price({ reservations, profileId, serviceId, enterpriseId });
        return dispatch(createFetchAction(BASKET_PRICE_FETCH, promise, { scope }));
    };

export const fetchServiceRelatedData =
    (serviceId: string): CommanderThunkAction<Promise<unknown>> =>
    async (dispatch, getState) => {
        const state = getState();
        const serviceIdProps = { serviceId };
        const enterpriseId = getServiceEnterpriseId(state, { id: serviceId });
        const profileId = getEnterpriseProfileId(state, { enterpriseId });
        const serviceIds = [serviceId];
        const enterpriseIds = [enterpriseId];

        const promiseArray = [
            dispatch(
                fetchActiveEnterpriseServices({
                    enterpriseIds,
                    serviceIds,
                    extent: {
                        products: true,
                        businessSegments: true,
                        ageCategories: true,
                    },
                })
            ),
            dispatch(fetchServiceVouchers({ enterpriseIds, serviceIds, extent: {}, type: VoucherType.Public })),
            !hasServiceRates(state, serviceIdProps) && dispatch(fetchRates({ serviceIds, profileId })),
        ];
        return Promise.all(compact(promiseArray));
    };

const shouldFetchAvailability = overEvery([
    negate(hasServiceAvailableCategories),
    // @ts-expect-error $TSFixMeError: Type '(a1: any, a2: { serviceId: string; }) => boolean' is not assignable to type '(...args: unknown[]) => boolean'.
    compose(negate(isEmpty), getResourceCategoryIds),
    // @ts-expect-error $TSFixMeError: Type '(state: unknown, { serviceId, availabilityBlockId }: { serviceId: any; availabilityBlockId: any; }) => boolean' is...
    (state, { serviceId, availabilityBlockId }) => {
        if (availabilityBlockId) {
            // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'ApplicationState'.
            const availabilityBlock = getAvailabilityBlock(state, { id: availabilityBlockId });
            // @ts-expect-error $TSFixMeError: Object is possibly 'undefined'.
            return serviceId === availabilityBlock.serviceId;
        }
        return true;
    },
]);

export const fetchAvailability =
    (serviceId: string, scope: string): CommanderThunkAction<AsyncAction> =>
    (dispatch, getState, { container, featureFlagService }) => {
        const state = getState();
        const availabilityBlockId = getSelectedAvailabilityBlockId(state, { scope });

        if (!shouldFetchAvailability(state, { serviceId, scope, availabilityBlockId, featureFlagService })) {
            return;
        }

        const { serviceComponent } = container();

        // @ts-expect-error $TSFixMeError: Argument of type '{ scope: any; id: any; }' is not assignable to parameter of type '{ id: string; }'.
        const enterpriseId = getServiceEnterpriseId(state, { scope, id: serviceId });
        const profileId = getEnterpriseProfileId(state, { enterpriseId });
        const startUtc = getServiceStartDateUtc(state, { scope, serviceId });
        const endUtc = getServiceEndDateUtc(state, { scope, serviceId });
        const options = { serviceId, profileId, startUtc, endUtc, availabilityBlockId };
        const isMultiTaxEnvironment = isEnterpriseMultiTaxEnvironment(state, { enterpriseId });

        dispatch(createFetchAction(AVAILABILITY_FETCH_STARTED, options, { scope }));
        return serviceComponent
            .getAvailability({ ...options, extent: { defaultPrice: true } })
            .then(({ categoryAvailabilities }: $TSFixMe) => {
                const callbackState = getState();
                const dateRangeKey = getDateRangeKeyFromOrder(callbackState, { scope });
                const collidingReservations = getCollidingReservationsByCategory(callbackState, { scope });
                const realCategoryAvailabilities = processCategoryAvailabilities(
                    collidingReservations,
                    isMultiTaxEnvironment
                )(categoryAvailabilities);
                dispatch(
                    createFetchAction(
                        AVAILABILITY_FETCH_SUCCEEDED,
                        { ...options, dateRangeKey, categoryAvailabilities: realCategoryAvailabilities },
                        { scope }
                    )
                );
            })
            .catch((error: $TSFixMe) => {
                dispatch(createFetchAction(AVAILABILITY_FETCH_FAILED, { ...options, error }, { scope }));
                throw error;
            });
    };

export const fetchRatePricing =
    ({ scope, resourceCategoryIds }: { scope: string; resourceCategoryIds?: string[] }): CommanderThunkAction =>
    (dispatch, getState, { container }) => {
        const state = getState();
        const { serviceComponent } = container();

        const serviceId = getServiceId(state, { scope });
        const showBanner =
            getShowDateServiceDiscrepancyBanner(state, { scope, serviceId }) ||
            getShowTimeServiceDiscrepancyBanner(state, { scope, serviceId });
        if (showBanner) {
            return;
        }
        const pricingOptions = getPricingOptions(state, { scope, serviceId });
        const { categoryOccupancies, ...otherPricingOptions } = pricingOptions;
        const filteredCategoryOccupancies =
            resourceCategoryIds && array.isNonEmpty(resourceCategoryIds)
                ? pipe(
                      categoryOccupancies,
                      // @ts-expect-error
                      array.filter(({ categoryId }) => resourceCategoryIds.includes(categoryId))
                  )
                : categoryOccupancies;

        const mappedCategoryOccupancies = pipe(
            // @ts-expect-error
            filteredCategoryOccupancies,
            // @ts-expect-error $TSFixMeError: Property 'occupancyData' does not exist on type 'PartialDeep<unknown>'.
            array.filterMap(({ occupancyData, ...other }) =>
                pipe(
                    occupancyData,
                    Object.values<OccupancyData>,
                    array.filter(({ personCount }) => personCount > 0),
                    (ageCategories) =>
                        array.isNonEmpty(ageCategories)
                            ? option.some({
                                  ...other,
                                  occupancyData: ageCategories,
                              })
                            : option.none
                )
            )
        );

        const isMultiTaxEnvironment = isSelectedEnterpriseInMultiTaxEnvironment(state, { scope });

        // @ts-expect-error $TSFixMeError: Argument of type '{ scope: any; id: any; }' is not assignable to parameter of type '{ id: string; }'.
        const enterpriseId = getServiceEnterpriseId(state, { scope, id: serviceId });
        const profileId = getEnterpriseProfileId(state, { enterpriseId });
        const availabilityBlockId = getSelectedAvailabilityBlockId(state, { scope });
        const resourceCategoryTypeCategoryIds = getResourceCategoryIdsOfSelectedResourceCategoryType(state, {
            scope,
            enterpriseId,
            profileId,
            serviceId,
        });

        const requestId = createUUID();
        dispatch(createFetchAction(PRICING_FETCH_STARTED, { ...pricingOptions, requestId }, { scope }));
        dispatch(createAction(CUSTOM_PRICE_CLEARED, undefined, { scope }));

        return serviceComponent
            .getRatePricing({
                ...otherPricingOptions,
                categoryOccupancies: mappedCategoryOccupancies,
                profileId,
                availabilityBlockId,
                extent: { vouchers: true },
            })
            .then(async (servicePricing: ServicePricing) => {
                const { vouchers } = servicePricing;
                const storedRateIds = getServiceRateIds(state, { serviceId });
                const fetchedRateIds = getRateIdsFromServicePricing(servicePricing);
                if (!isDifferenceEmpty(fetchedRateIds, storedRateIds)) {
                    await dispatch(fetchRates({ serviceIds: [serviceId], profileId }));
                }

                if (vouchers?.length) {
                    dispatch(setVouchers(vouchers));
                }

                dispatch(
                    createFetchAction(
                        PRICING_FETCH_SUCCEEDED,
                        {
                            ...otherPricingOptions,
                            categoryOccupancies,
                            isMultiTaxEnvironment,
                            servicePricing,
                            resourceCategoryTypeCategoryIds,
                            requestId,
                        },
                        { scope }
                    )
                );
            })
            .catch((error: $TSFixMe) => {
                dispatch(createFetchAction(PRICING_FETCH_FAILED, { ...pricingOptions, error, requestId }, { scope }));
                handleErrorPure(error);
                throw error;
            });
    };

const getConstraints = (
    state: ApplicationState,
    { serviceId, scope, appClockService }: WithServiceId & WithScope & AppClockServiceProps
) => {
    // @ts-expect-error
    const isDefiniteState = isDefinite(state, { serviceId, scope });
    // @ts-expect-error
    const baseConstraints = getOptionConstraints(state, { serviceId, scope });

    if (isDefiniteState) {
        return baseConstraints;
    }

    const reservationMinArrivalUtc = getReservationsMinimalArrivalUtc(state, {
        id: serviceId,
        // @ts-expect-error
        serviceId,
        scope,
        appClockService,
    });
    return reduce((acc, key) => {
        // @ts-expect-error $TSFixMeError: A computed property name must be of type 'string', 'number', 'symbol', or 'any'.
        const { [key]: releaseConstraints } = getOptionConstraints(state, {
            serviceId,
            scope,
            // @ts-expect-error
            reservationMinArrivalUtc,
            // @ts-expect-error
            key,
        });
        return {
            ...acc,
            // @ts-expect-error $TSFixMeError: A computed property name must be of type 'string', 'number', 'symbol', or 'any'.
            [key]: releaseConstraints,
        };
    }, baseConstraints)(['releaseDate', 'releaseTime']);
};

// TODO should be split into 2 actions: pure api call & the handling around
export const createReservationGroup =
    (scope: string): CommanderThunkAction =>
    async (dispatch, getState, { container, loggerService, resolveRoute, appClockService, gainsightService }) => {
        const state = getState();
        const serviceId = getServiceId(state, { scope });
        const props = { scope, serviceId };

        const optionValues = getOptionValues(state, props);
        const constraints = getConstraints(state, { ...props, appClockService });
        // @ts-expect-error $TSFixMeError: Expected 2 arguments, but got 1.
        const errors: object = validate(constraints)(optionValues);

        dispatch(createAction(OPTIONS_VALIDATED, errors, props));

        if (!isEmpty(errors)) {
            showErrorAlert({ description: lt('ValidationErrorMessage') });
        } else {
            const { reservationGroupComponent } = container();

            const { reservations, ...otherGroupProps } = getReservationGroup(state, props);
            // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
            const profileId = getSelectedServiceUserProfileId(state, props);
            const enterpriseId = getServiceEnterpriseId(state, { id: serviceId });
            const enterpriseChainId = getEnterpriseChainId(state, { enterpriseId });
            const currentEnterpriseId = getCurrentEnterpriseId();
            const currentChainId = getCurrentEnterpriseChainId(state);

            const mappedReservations = map(
                ({ occupancyData, ...other }) => ({
                    ...other,
                    occupancyData: values(occupancyData),
                }),
                reservations
            );

            const promise = reservationGroupComponent
                .create({ ...otherGroupProps, reservations: mappedReservations, profileId, enterpriseId })
                .then((result: $TSFixMe) => {
                    const { id } = result;
                    const url = resolveRoute(RouteName.ReservationGroupDetail, {
                        id,
                        enterpriseId,
                        currentEnterpriseId: enterpriseId,
                    });

                    if (isCrossEnterpriseReservation(state, props)) {
                        dispatch(showConfirmation(url, scope));
                    } else {
                        dispatch(navigateInModal(url));
                        showSuccessAlert({ description: lt('ReservationCreatedSuccessMessage') });
                    }

                    const uniqueServiceIds = [...new Set(reservations.map((reservation) => reservation.serviceId))];
                    if (uniqueServiceIds.length > 1) {
                        gainsightService.trackEvent('MultipleServicesReservationCreationSubmitSuccess', {
                            serviceAmount: uniqueServiceIds.length,
                        });
                    }

                    window.performance.mark(TIME_TO_RESERVATION_END_MARK);
                    window.performance.measure(
                        'TimeToReservation',
                        TIME_TO_RESERVATION_START_MARK,
                        TIME_TO_RESERVATION_END_MARK
                    );

                    const [{ duration }] = window.performance.getEntriesByType('measure');

                    loggerService.log(NEW_RESERVATION_SCREEN_EVENT_CATEGORY, 'time-to-reservation', {
                        duration,
                        serviceId,
                        currentChainId,
                        currentEnterpriseId,
                        enterpriseChainId,
                        enterpriseId,
                    });

                    window.performance.clearMarks(TIME_TO_RESERVATION_START_MARK);
                    window.performance.clearMarks(TIME_TO_RESERVATION_END_MARK);
                    window.performance.clearMeasures();

                    return result;
                });

            return dispatch(createFetchAction(CREATE_RESERVATION_GROUP, promise, props));
        }
    };

export const overrideReservationPrice =
    ({ rateId, categoryId, customNightCost }: $TSFixMe, scope: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const state = getState();

        const serviceId = getServiceId(state, { scope });
        const enterpriseId = getServiceEnterpriseId(state, { id: serviceId });
        const profileId = getEnterpriseProfileId(state, { enterpriseId });
        const isMultiTaxEnvironment = isSelectedEnterpriseInMultiTaxEnvironment(state, { scope });

        const { reservationGroupComponent, appLocalizationComponent } = container();
        const parsedCustomNightCost = update('value', appLocalizationComponent.parseCurrencyValue, customNightCost);

        const reservationWithCustomPrice = getReservationWithCustomPrice(state, {
            // @ts-expect-error $TSFixMeError: Argument of type '{ rateId: any; categoryId: any; customNightCost: _.LodashUpdate1x5; scope: any; serviceId: any; }' is ...
            rateId,
            categoryId,
            customNightCost: parsedCustomNightCost,
            scope,
            serviceId,
        });

        const apiCallId = createUUID();

        const promise = reservationGroupComponent
            .price({ profileId, enterpriseId, serviceId, reservations: [reservationWithCustomPrice] })
            .then((data: $TSFixMe) => ({
                data,
                rateId,
                categoryId,
                isMultiTaxEnvironment,
                apiCallId,
            }));
        return dispatch(
            createFetchAction(CUSTOM_PRICE_FETCH, { promise, data: { rateId, categoryId, apiCallId } }, { scope })
        );
    };

const showConfirmation = (url: $TSFixMe, scope: $TSFixMe) => createAction(CONFIRMATION_SHOWN, { url }, { scope });
